import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import MemberLayout from '../components/MemberLayout';
import './Profil.scss';

const toDisplay = (d) => {
  if (!d) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
    const [y, m, day] = d.substring(0, 10).split('-');
    return `${day}/${m}/${y}`;
  }
  return d;
};

const toISO = (d) => {
  if (!d) return '';
  const m = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

const Profil = () => {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    universite: user?.universite || '',
    filiere: user?.filiere || '',
    niveau: user?.niveau || '',
    date_naissance: toDisplay(user?.date_naissance) || ''
  });

  const [photoPreview, setPhotoPreview] = useState(user?.photo || null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('La photo ne doit pas dépasser 2 Mo.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    setError('');
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await api.post('/profile/photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({ photo: data.photo });
    } catch {
      setError('Erreur lors de l\'upload de la photo.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Validation date JJ/MM/AAAA
    if (formData.date_naissance) {
      const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      if (!dateRegex.test(formData.date_naissance)) {
        setError('La date de naissance doit être au format JJ/MM/AAAA.');
        setSaving(false);
        return;
      }
      const [, dd, mm, yyyy] = formData.date_naissance.match(dateRegex);
      const date = new Date(`${yyyy}-${mm}-${dd}`);
      if (isNaN(date.getTime())) {
        setError('Date de naissance invalide.');
        setSaving(false);
        return;
      }
    }

    try {
      const payload = { ...formData, date_naissance: toISO(formData.date_naissance) };
      const { data } = await api.put('/profile', payload);
      updateUser(data.user);
      setSuccess('Profil mis à jour avec succès !');
    } catch (err) {
      const msg = err.response?.data?.message
        || Object.values(err.response?.data?.errors || {})[0]?.[0]
        || 'Erreur lors de la sauvegarde.';
      setError(msg);
    }
    setSaving(false);
  };

  const initials = `${user?.prenom?.charAt(0) || ''}${user?.nom?.charAt(0) || ''}`;

  return (
    <MemberLayout>
      <div className="profil-container">
        <div className="profil-header">
          <h1>Mon Profil</h1>
          <p>Gérez vos informations personnelles</p>
        </div>

        <div className="profil-grid">
          {/* Colonne photo */}
          <aside className="profil-photo-col">
            <div className="photo-card">
              <div className="photo-wrapper" onClick={() => fileInputRef.current?.click()}>
                {photoPreview ? (
                  <img src={photoPreview} alt={`${user?.prenom} ${user?.nom}`} className="photo-preview" />
                ) : (
                  <div className="photo-initials">{initials}</div>
                )}
                <div className="photo-overlay">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <span>Modifier</span>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handlePhotoChange}
              />
              <p className="photo-hint">JPG, PNG ou WEBP · max 2 Mo</p>
              <p className="photo-name">{user?.prenom} {user?.nom}</p>
              <p className="photo-role">Membre BE TALENTED</p>
            </div>
          </aside>

          {/* Formulaire */}
          <div className="profil-form-col">
            <form onSubmit={handleSubmit} className="profil-form">
              {success && <div className="alert alert-success">{success}</div>}
              {error   && <div className="alert alert-error">{error}</div>}

              <div className="form-section">
                <h3>Informations personnelles</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nom</label>
                    <input type="text" name="nom" value={formData.nom} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Prénom</label>
                    <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Téléphone</label>
                    <input type="tel" name="telephone" value={formData.telephone} onChange={handleChange} placeholder="+225 XX XX XX XX XX" />
                  </div>
                  <div className="form-group">
                    <label>
                      Date de naissance
                      <span className="label-hint">JJ/MM/AAAA</span>
                    </label>
                    <input
                      type="text"
                      name="date_naissance"
                      value={formData.date_naissance}
                      onChange={handleChange}
                      placeholder="JJ/MM/AAAA"
                      maxLength={10}
                      pattern="\d{2}/\d{2}/\d{4}"
                      onInput={(e) => {
                        let v = e.target.value.replace(/\D/g, '');
                        if (v.length > 2) v = v.slice(0,2) + '/' + v.slice(2);
                        if (v.length > 5) v = v.slice(0,5) + '/' + v.slice(5);
                        e.target.value = v.slice(0, 10);
                        setFormData((f) => ({ ...f, date_naissance: e.target.value }));
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Informations académiques</h3>
                <div className="form-group">
                  <label>Université / École</label>
                  <input type="text" name="universite" value={formData.universite} onChange={handleChange} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Filière</label>
                    <input type="text" name="filiere" value={formData.filiere} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Niveau</label>
                    <select name="niveau" value={formData.niveau} onChange={handleChange}>
                      <option value="">Sélectionnez</option>
                      <option value="licence1">Licence 1</option>
                      <option value="licence2">Licence 2</option>
                      <option value="licence3">Licence 3</option>
                      <option value="master1">Master 1</option>
                      <option value="master2">Master 2</option>
                      <option value="doctorat">Doctorat</option>
                    </select>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </MemberLayout>
  );
};

export default Profil;
